import { useFetcher, useFetchers, useNavigation } from '@remix-run/react'

import React from 'react'
import { getFetcherType } from 'remix-utils/fetcher-type'
import { type GetCustomer } from '~/models/ecommerce-providers/sylius/shop/fetch/customer/get-customer'
import { type AddressNormalized } from '~/models/ecommerce-providers/sylius/shop/interface/address'
import {
	PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS,
	PROFILE_ADDRESSES_INPUTS,
} from '~/routes/($lang).profile.addresses/addresses.component'
import {
	PROFILE_ADDRESSES_CREATE_FORM,
	PROFILE_ADDRESSES_CREATE_INPUTS,
} from '~/routes/($lang).profile.addresses.create/create.component'
import { ROOT_INPUTS } from '~/routes/_layout/root.component'

type useOptimisticDefaultAddressProps = {
	customer: GetCustomer
	iri: string
	id: number
}

function useOptimisticDefaultAddress({
	customer,
	iri,
	id,
}: useOptimisticDefaultAddressProps) {
	const [optimistic, setOptimistic] = React.useState(
		(customer!.defaultAddress as AddressNormalized | null)?.['@id'] === iri,
	)
	const defaultAddressFetcher = useFetcher({
		key: PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS.generateFetcherKey!({
			id: String(id),
		}),
	})
	const createAddressFetcher = useFetcher({
		key: PROFILE_ADDRESSES_CREATE_FORM.fetcherKey,
	})
	const fetchers = useFetchers()
	const navigation = useNavigation()
	const defaultAddressFetcherType = getFetcherType(
		defaultAddressFetcher,
		navigation,
	)

	React.useEffect(() => {
		if (defaultAddressFetcherType == 'init') {
			setOptimistic(
				(customer!.defaultAddress as AddressNormalized | null)?.['@id'] === iri,
			)
		} else if (defaultAddressFetcher.formData) {
			setOptimistic(
				defaultAddressFetcher.formData?.get(
					PROFILE_ADDRESSES_INPUTS.defaultAddress,
				) === iri,
			)
		}
	}, [
		defaultAddressFetcherType,
		setOptimistic,
		customer,
		iri,
		defaultAddressFetcher,
	])

	return optimistic

	return (
		((customer!.defaultAddress as AddressNormalized | null)?.['@id'] === iri &&
			!fetchers.find(
				(fetcher) =>
					fetcher.formData?.get(ROOT_INPUTS.subaction) ===
					PROFILE_ADDRESSES_FORM_SET_DEFAULT_ADDRESS.subaction,
			) &&
			!createAddressFetcher.formData?.has(
				PROFILE_ADDRESSES_CREATE_INPUTS.defaultAddress,
			)) ||
		defaultAddressFetcher.formData?.get(
			PROFILE_ADDRESSES_INPUTS.defaultAddress,
		) === iri ||
		(createAddressFetcher.formData?.has(
			PROFILE_ADDRESSES_CREATE_INPUTS.defaultAddress,
		) &&
			id === -1) ||
		!customer?.defaultAddress
	)
}

export { useOptimisticDefaultAddress }
